import { useState, useCallback } from "react";
import { notification } from "antd";
import { NOTIFICATION_MESSAGES } from "@shared/utils";
import {
  IntegrationCard,
  IntegrationStatusTypes,
  LastAction,
  IntegrationAttributes,
  ActionHistoryItem,
} from "../types/integration-card";

interface UseIntegrationCardsReturn {
  cards: IntegrationCard[];
  isLoading: boolean;
  connectCard: (cardId: string) => Promise<void>;
  saveCard: (cardId: string) => Promise<void>;
  disconnectCard: (cardId: string) => Promise<void>;
  enableEditMode: (cardId: string) => void;
  cancelEditMode: (cardId: string) => void;
  copyId: (id: string) => void;
  updateCard: (cardId: string, updates: Partial<IntegrationCard>) => void;
}

const getCurrentUser = () => {
  return "Текущий пользователь";
};

const formatDateTime = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${day}.${month}.${year} ${hours}:${minutes} GMT+3`;
};

export const useIntegrationCards = (
  initialCards: IntegrationCard[],
): UseIntegrationCardsReturn => {
  const [cards, setCards] = useState<IntegrationCard[]>(initialCards);
  const [isLoading, setIsLoading] = useState(false);

  const addActionHistory = useCallback(
    (
      cardId: string,
      status: "Подключена" | "Отключена",
      organName: string,
    ) => {
      const currentDate = new Date();
      const historyItem: ActionHistoryItem = {
        id: `history-${Date.now()}`,
        dateTime: formatDateTime(currentDate),
        user: getCurrentUser(),
        position: "Начальник",
        status,
        organName,
      };

      setCards((prevCards) =>
        prevCards.map((card) =>
          card.id === cardId
            ? {
                ...card,
                actionHistory: [historyItem, ...(card.actionHistory || [])],
              }
            : card,
        ),
      );
    },
    [],
  );

  const connectCard = useCallback(
    async (cardId: string) => {
      setIsLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000)); // eslint-disable-line no-promise-executor-return

        setCards((prevCards) =>
          prevCards.map((card) =>
            card.id === cardId
              ? {
                  ...card,
                  status: IntegrationStatusTypes.CONNECTED,
                  isEditMode: false,
                }
              : card,
          ),
        );

        const card = cards.find((c) => c.id === cardId);
        if (card?.selectedOrgan) {
          addActionHistory(cardId, "Подключена", card.selectedOrgan.name);
        }

        notification.success({
          message: NOTIFICATION_MESSAGES.INTEGRATION_CONNECTED, // "Интеграция подключена"
          description: NOTIFICATION_MESSAGES.INTEGRATION_CONNECTED_DESC, // "Настройки интеграции успешно сохранены"
        });
      } catch (error) {
        notification.error({
          message: NOTIFICATION_MESSAGES.INTEGRATION_CONNECT_FAILED, // "Ошибка при подключении"
          description: NOTIFICATION_MESSAGES.INTEGRATION_CONNECT_FAILED_DESC, // "Не удалось подключить интеграцию. Попробуйте еще раз."
        });
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [cards, addActionHistory],
  );

  const saveCard = useCallback(
    async (cardId: string) => {
      setIsLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000)); // eslint-disable-line no-promise-executor-return

        const currentDate = new Date();
        const lastAction: LastAction = {
          user: getCurrentUser(),
          date: currentDate.toLocaleDateString("ru-RU"),
          time: `${currentDate.toLocaleTimeString("ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "Europe/Moscow",
          })} МСК`,
        };

        const card = cards.find((c) => c.id === cardId);
        const attributes: IntegrationAttributes = {
          connectionId: card?.attributes?.connectionId || "32498723-WEF",
          version: card?.attributes?.version || "13.0",
          connectionPath: card?.attributes?.connectionPath || "https://12345.com",
        };

        setCards((prevCards) =>
          prevCards.map((card) =>
            card.id === cardId
              ? {
                  ...card,
                  status: IntegrationStatusTypes.CONNECTED,
                  lastAction,
                  attributes,
                  isEditMode: false,
                }
              : card,
          ),
        );

        if (card?.selectedOrgan) {
          addActionHistory(cardId, "Подключена", card.selectedOrgan.name);
        }

        notification.success({
          message: NOTIFICATION_MESSAGES.INTEGRATION_SETTINGS_SAVED, // "Настройки сохранены"
          description: NOTIFICATION_MESSAGES.INTEGRATION_SETTINGS_SAVED_DESC, // "Интеграционные настройки успешно сохранены"
        });
      } catch (error) {
        notification.error({
          message: NOTIFICATION_MESSAGES.INTEGRATION_SETTINGS_SAVE_FAILED, // "Ошибка при сохранении"
          description: NOTIFICATION_MESSAGES.INTEGRATION_SETTINGS_SAVE_FAILED_DESC, // "Не удалось сохранить настройки интеграции. Попробуйте еще раз."
        });
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [cards, addActionHistory],
  );

  const disconnectCard = useCallback(
    async (cardId: string) => {
      setIsLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000)); // eslint-disable-line no-promise-executor-return

        const card = cards.find((c) => c.id === cardId);
        setCards((prevCards) =>
          prevCards.map((card) =>
            card.id === cardId
              ? {
                  ...card,
                  status: IntegrationStatusTypes.DISCONNECTED,
                  isEditMode: false,
                }
              : card,
          ),
        );

        if (card?.selectedOrgan) {
          addActionHistory(cardId, "Отключена", card.selectedOrgan.name);
        }

        notification.success({
          message: NOTIFICATION_MESSAGES.INTEGRATION_DISCONNECTED, // "Интеграция отключена"
          description: NOTIFICATION_MESSAGES.INTEGRATION_DISCONNECTED_DESC, // "Интеграция успешно отключена"
        });
      } catch (error) {
        notification.error({
          message: NOTIFICATION_MESSAGES.INTEGRATION_DISCONNECT_FAILED, // "Ошибка при отключении"
          description: NOTIFICATION_MESSAGES.INTEGRATION_DISCONNECT_FAILED_DESC, // "Не удалось отключить интеграцию. Попробуйте еще раз."
        });
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [cards, addActionHistory],
  );

  const enableEditMode = useCallback((cardId: string) => {
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.id === cardId ? { ...card, isEditMode: true } : card,
      ),
    );
  }, []);

  const cancelEditMode = useCallback((cardId: string) => {
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.id === cardId ? { ...card, isEditMode: false } : card,
      ),
    );
  }, []);

  const copyId = useCallback((id: string) => {
    navigator.clipboard
      .writeText(id)
      .then(() => {
        notification.success({
          message: NOTIFICATION_MESSAGES.INTEGRATION_ID_COPIED, // "ID скопирован"
          description: NOTIFICATION_MESSAGES.INTEGRATION_ID_COPIED_DESC, // "Идентификатор скопирован в буфер обмена"
        });
      })
      .catch(() => {
        notification.error({
          message: NOTIFICATION_MESSAGES.INTEGRATION_ID_COPY_FAILED, // "Ошибка копирования"
          description: NOTIFICATION_MESSAGES.INTEGRATION_ID_COPY_FAILED_DESC, // "Не удалось скопировать ID в буфер обмена"
        });
      });
  }, []);

  const updateCard = useCallback(
    (cardId: string, updates: Partial<IntegrationCard>) => {
      setCards((prevCards) =>
        prevCards.map((card) =>
          card.id === cardId ? { ...card, ...updates } : card,
        ),
      );
    },
    [],
  );

  return {
    cards,
    isLoading,
    connectCard,
    saveCard,
    disconnectCard,
    enableEditMode,
    cancelEditMode,
    copyId,
    updateCard,
  };
};

